import { useEffect, useState } from "react";
import { AlertCircle, ExternalLink } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { ComputerAgentData } from "../stores";
import { detectResources, RESOURCE_LABELS, RESOURCE_ENTITY_TYPE } from "../utils/resourceDetection";

import { PulseDot } from "./PulseDot";

import { Button } from "@/components/ui/button";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { ComputerAgentDataStatus, MessageRole } from "@/components/VibeChat/types";
import { ChatMessage as PlaygroundChatMessage } from "@/components/Playground/Drawer/Chat/ChatMessage";
import { convertToChatMessageI } from "@/components/VibeChat/utils/parsers";
import { cn } from "@/lib/utils";
import { CopyButton } from "@/components/ui/copy-button";
interface ComputerAgentWidgetProps {
  computerAgent: ComputerAgentData;
  isStreaming?: boolean;
  index?: number;
  compact?: boolean;
}

const formatElapsed = (ms: number): string => {
  const duration = moment.duration(ms);
  const minutes = Math.floor(duration.asMinutes());
  const seconds = duration.seconds();
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const WRANGLER_AGENT_ICON = (
  <img src="/wrangler-ai-icon.png" alt="Wrangler AI" className="h-5 w-5 object-contain" />
);

export const ComputerAgentWidget = ({
  computerAgent,
  isStreaming: _isStreaming = false,
  index,
  compact = true,
}: ComputerAgentWidgetProps) => {
  const { status, error, progressPhrase, results, startedAt } = computerAgent;
  const humanReadableParagraph =
    computerAgent.humanReadableParagraph ?? results?.human_readable_paragraph;

  const isCompleted = status === ComputerAgentDataStatus.COMPLETED;
  const isError = status === ComputerAgentDataStatus.ERROR;
  const isCancelled = status === ComputerAgentDataStatus.CANCELLED;

  const navigate = useNavigate();
  const workspace = useActiveWorkspace();

  const [elapsed, setElapsed] = useState(() => (startedAt ? Date.now() - startedAt : 0));

  useEffect(() => {
    if (status !== ComputerAgentDataStatus.RUNNING || !startedAt) {
      return;
    }
    const interval = setInterval(() => setElapsed(Date.now() - startedAt), 1000);
    return () => clearInterval(interval);
  }, [status, startedAt]);

  if (isCompleted) {
    let workspaceId: number | null = null;
    if (workspace?.id) {
      workspaceId = typeof workspace.id === "string" ? parseInt(workspace.id, 10) : workspace.id;
    }
    const resources = results && workspaceId ? detectResources(results, workspaceId) : [];

    return (
      <div className={cn("relative my-0.5 flex flex-col gap-1", humanReadableParagraph && "pb-4")}>
        <div className={cn("flex items-center gap-2")}>
          <PulseDot variant="green" className="m-0.5" />
          <span className="text-xs text-gray-500">Agent Completed</span>
        </div>

        {humanReadableParagraph && (
          <div className="flex flex-col items-start gap-1">
            <PlaygroundChatMessage
              hideTimestamp
              hideCopyButton
              message={convertToChatMessageI(
                { ...computerAgent, content: humanReadableParagraph, role: MessageRole.ASSISTANT },
                index || 0,
              )}
              isStreaming={false}
              isMinimal
              customAgentIcon={WRANGLER_AGENT_ICON}
            />

            <div className="absolute bottom-[14px] left-0 ml-1 flex items-center gap-0.5">
              <CopyButton text={humanReadableParagraph} variant="ghost" iconSize="h-4 w-4" />
            </div>
          </div>
        )}

        {resources.length > 0 && (
          <div className={cn("ml-5 flex flex-wrap gap-1.5", humanReadableParagraph && "ml-11")}>
            {resources.map(resource => {
              const entityType = RESOURCE_ENTITY_TYPE[resource.type];
              const label = RESOURCE_LABELS[resource.type];
              return (
                <BasicTooltip
                  key={`${resource.type}-${resource.id}`}
                  content={`${label}: ${resource.name}`}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(resource.url)}
                    className="h-7 max-w-full gap-1.5 text-xs"
                  >
                    <EntityIcon type={entityType} size="xs" />
                    <span className={cn("max-w-full", compact && "truncate")}>
                      {label}: {resource.name}
                    </span>

                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </BasicTooltip>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="my-0.5">
        <div className="flex items-center gap-2">
          <PulseDot variant="red" className="m-0.5" />
          <span className="text-xs text-red-500">Something went wrong on screen</span>
        </div>
        {error && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <div className="flex-1 text-xs text-red-800">
              <div className="font-medium text-red-900">Error</div>
              <div className="mt-1">{error}</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (isCancelled) {
    return (
      <div className="my-0.5 flex items-center gap-2">
        <PulseDot variant="gray" className="m-0.5" />
        <span className="text-xs text-gray-500">Agent Stopped</span>
      </div>
    );
  }

  const displayPhrase = progressPhrase || "Agent Working...";

  return (
    <div className="my-0.5 flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <PulseDot variant="blue" isAnimated className="m-0.5" />
        <div className={cn("flex items-center", compact && "h-4 overflow-hidden")}>
          <AnimatePresence mode="wait">
            <motion.span
              key={progressPhrase || "default"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="block text-xs text-gray-600"
            >
              {displayPhrase}
            </motion.span>
          </AnimatePresence>
        </div>
        {startedAt && (
          <span className="text-xs tabular-nums text-gray-400">{formatElapsed(elapsed)}</span>
        )}
      </div>

      <AnimatePresence mode="wait">
        {humanReadableParagraph && (
          <motion.p
            key={humanReadableParagraph}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="ml-5 text-xs leading-relaxed text-gray-400"
          >
            {humanReadableParagraph}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ComputerAgentWidget;
